/**
 * How the cache sync changes a list the user can see (shipcast-nad4).
 *
 * Writing a whole field array with `setValue` gives every row a new key, so
 * React remounts the list: the input the user is typing in disappears, and a
 * row being dragged is dropped. `useFieldArray`'s own operations keep the
 * keys, so the rows — and the caret, the unsaved text, the drag — move with
 * their data. Each mounted list therefore lends its operations to the sync
 * through this registry; a list with no mounted owner (another team's
 * deliverables) has nothing on screen to disturb, and the sync writes it with
 * `setValue`.
 *
 * A list being dragged is left alone until the drop: the sync skips it and the
 * owner reports the drop, after which the list is brought up to date.
 */

import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import {
  useFieldArray,
  type ArrayPath,
  type FieldArray,
  type UseFieldArrayProps,
  type UseFieldArrayReturn,
} from 'react-hook-form';
import type { SimulationFormValues } from '@/types/api';

/** The operations of one mounted list, by index, as `useFieldArray` has them. */
export interface ListOperations {
  move: (from: number, to: number) => void;
  insert: (index: number, value: unknown) => void;
  remove: (index: number) => void;
}

interface Owner {
  operations: ListOperations;
  isDragging: () => boolean;
}

/** Called when the user drops a row in the list at `path`. */
export type DropListener = (path: string, moved: boolean) => void;

export interface FieldArrayRegistry {
  /** The mounted owner of the list at `path`, if any. */
  owner: (path: string) => Owner | undefined;
  /** Returns the unsubscribe function. */
  onDrop: (listener: DropListener) => () => void;
}

interface MutableRegistry extends FieldArrayRegistry {
  register: (path: string, owner: Owner) => () => void;
  dropped: (path: string, moved: boolean) => void;
}

export function createFieldArrayRegistry(): MutableRegistry {
  const owners = new Map<string, Owner>();
  const listeners = new Set<DropListener>();
  return {
    owner: (path) => owners.get(path),
    onDrop: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    register: (path, owner) => {
      owners.set(path, owner);
      return () => {
        if (owners.get(path) === owner) owners.delete(path);
      };
    },
    dropped: (path, moved) => {
      for (const listener of listeners) listener(path, moved);
    },
  };
}

/** Provided by SimulationForm around its lists. */
export const FieldArrayRegistryContext = createContext<MutableRegistry | null>(null);

/** Drag lifecycle of a sortable list, for its `DndContext`. */
export interface ListDrag {
  /** `DndContext`'s `onDragStart`. */
  started: () => void;
  /** Call once the drop is handled — after the local move, if any. */
  ended: (moved: boolean) => void;
}

/**
 * `useFieldArray`, with the list's operations lent to the cache sync. Pass the
 * registry explicitly from the component that creates it; children take it
 * from context.
 */
export function useSyncedFieldArray<TName extends ArrayPath<SimulationFormValues>>(
  props: UseFieldArrayProps<SimulationFormValues, TName>,
  explicitRegistry?: MutableRegistry,
): UseFieldArrayReturn<SimulationFormValues, TName> & { drag: ListDrag } {
  const contextRegistry = useContext(FieldArrayRegistryContext);
  const registry = explicitRegistry ?? contextRegistry;
  const fieldArray = useFieldArray(props);
  const { move, insert, remove } = fieldArray;
  const dragging = useRef(false);
  const path = props.name;

  useEffect(
    () =>
      registry?.register(path, {
        operations: {
          move,
          // Never focus the inserted row: the user's caret stays where it is.
          insert: (index, value) =>
            insert(index, value as FieldArray<SimulationFormValues, TName>, { shouldFocus: false }),
          remove,
        },
        isDragging: () => dragging.current,
      }),
    [registry, path, move, insert, remove],
  );

  const drag = useMemo<ListDrag>(
    () => ({
      started: () => {
        dragging.current = true;
      },
      ended: (moved) => {
        dragging.current = false;
        registry?.dropped(path, moved);
      },
    }),
    [registry, path],
  );

  return { ...fieldArray, drag };
}
